
import React, { useState } from 'react';
import SEO from '../SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Activity, BarChart2, CheckCircle, Check, X, ChevronDown, ChevronUp, Globe, MousePointer, Shield, TrendingUp, Zap, Target, Layout, Clock, Play, Server, Lock, Code , Star} from 'lucide-react';

interface LandingPageProps {
    onLogin?: () => void;
    onNavigate?: (page: string) => void;
}

const HeOrganicWebsiteTraffic: React.FC<LandingPageProps> = ({ onLogin, onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);

    // Schema.org Structured Data
    const schema = [
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "מחולל תנועה אורגנית לאתר",
            "description": "שירות תנועה אורגנית בסגנון חיפוש עם כתובות IP מגורים, בקרות זמן שהייה ודיווח גלוי ב-GA4.",
            "brand": { "@type": "Brand", "name": "Traffic Creator" }
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "בית", "item": "https://traffic-creator.com" },
                { "@type": "ListItem", "position": 2, "name": "תנועה אורגנית לאתר", "item": "https://traffic-creator.com/he/organic-website-traffic" }
            ]
        }
    ];

    const benefits = [
        {
            icon: <Search className="w-6 h-6 text-[#ff5a0a]" />,
            title: "ניתוח CTR אורגני",
            desc: "הקמפיינים מדמים מסלולי חיפוש וקליקים כדי לעזור לך לנתח דפוסי מעורבות, לא להבטיח מיקום מסוים בתוצאות."
        },
        {
            icon: <Clock className="w-6 h-6 text-[#ff5a0a]" />,
            title: "הגדלת זמן שהייה באתר",
            desc: "המבקרים נשארים באתר שלך דקות, גוללים בתוכן ומתקשרים, מה שמפחית דרמטית את שיעורי הנטישה."
        },
        {
            icon: <Globe className="w-6 h-6 text-[#ff5a0a]" />,
            title: "בדיקת תנועה לוקאלית",
            desc: "מקד תנועה מערים או אזורים ספציפיים כדי להשוות התנהגות משתמשים לפי מיקום גיאוגרפי."
        },
        {
            icon: <Shield className="w-6 h-6 text-[#ff5a0a]" />,
            title: "דפוסי Session מגוונים",
            desc: "הגדר תנועות עכבר, גלילה ולחיצות כדי ליצור דפוסי Session מגוונים יותר לבדיקת Analytics."
        }
    ];

    const comparisons = [
        { feature: "מקור תנועה", us: "כתובות IP מגורים אמיתיות", them: "פרוקסי של חוות שרתים" },
        { feature: "התנהגות", us: "גלילה, לחיצות, קריאה", them: "כניסות סטטיות" },
        { feature: "Google Analytics", us: "מזוהה ב-100%", them: "לעיתים מסונן / בוט" },
        { feature: "ניתוח SEO", us: "נתוני Session מפורטים", them: "ניטרלי או נמוך" },
        { feature: "בקרות איכות", us: "מקורות מגורים ובקרה", them: "סיכון דיווח גבוה" },
    ];

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 font-sans overflow-x-hidden" dir="rtl">
            <SEO
                title="תנועה אורגנית לאתר עם Sessions גלויים ב-GA4"
                description="קבל תנועה אורגנית בסגנון חיפוש עם IP מגורים, בקרות זמן שהייה, גלילה, קליקים, מיקוד גיאוגרפי ודיווח GA4 ברור לניתוח עמודים."
                keywords="תנועה אורגנית לאתר 2026, רכישת תנועה אורגנית, תנועת seo, ניתוח ctr, מדדי מעורבות, מבקרים לאתר, בוט תנועה ai"
                schema={schema}
                type="product"
            />

            {/* Hreflang Tags */}

            {/* Navigation */}
            <nav className="fixed w-full top-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-gray-100/50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                        <Link to="/login" className="bg-black text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#ff5a0a] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center rounded-sm">
                            התחברות לדשבורד
                        </Link>
                    </div>
                    <div className="flex items-center gap-8">
                        <div className="hidden md:flex gap-8 text-sm font-bold uppercase tracking-wide text-gray-500">
                            <Link to="/#pricing" className="hover:text-[#ff5a0a] transition-colors relative group">
                                מחירים
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </Link>
                            <a href="#benefits" className="hover:text-[#ff5a0a] transition-colors relative group">
                                תכונות
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <a href="#how-it-works" className="hover:text-[#ff5a0a] transition-colors relative group">
                                איך זה עובד
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                        <Link to="/" className="flex items-center gap-2">
                            <span className="hidden sm:inline-block text-xs font-bold bg-black text-white px-2 py-1 rounded-sm uppercase tracking-wide">Creator</span>
                            <span className="text-2xl font-black text-[#ff5a0a] tracking-tight group-hover:scale-105 transition-transform">TRAFFIC</span>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-32 px-6 relative overflow-hidden bg-white">
                {/* Background Decor */}
                <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none rounded-bl-[100px]"></div>
                <div className="absolute top-40 left-10 w-4 h-4 rounded-full bg-orange-200 animate-pulse"></div>
                <div className="absolute top-60 right-20 w-3 h-3 rounded-full bg-blue-200 animate-bounce delay-700"></div>

                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                    <div className="relative h-[550px] perspective-1000 group">
                        {/* Card Reflection */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-gray-100 to-gray-50 transform rotate-6 scale-95 opacity-50 rounded-2xl border border-gray-100 transition-transform duration-700 group-hover:rotate-3 group-hover:scale-95"></div>

                        {/* Main Interface Card */}
                        <div className="absolute inset-0 bg-white border border-gray-200 shadow-2xl rounded-2xl p-6 overflow-hidden transform transition-transform duration-700 group-hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
                            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10 pointer-events-none"></div>

                            {/* Fake Browser Header */}
                            <div className="border-b border-gray-100 pb-4 mb-6 flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400/80"></div>
                                <div className="mr-6 bg-gray-100 rounded-lg h-5 w-64 flex items-center px-2">
                                    <Lock size={10} className="text-gray-400 ml-2" />
                                    <div className="w-32 h-2 bg-gray-200 rounded-sm"></div>
                                </div>
                            </div>

                            {/* SERP Simulation */}
                            <div className="space-y-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className={`p-5 rounded-lg border transition-all duration-500 hover:shadow-lg cursor-default relative overflow-hidden ${i === 1 ? 'border-[#ff5a0a]/20 bg-orange-50/30' : 'border-gray-50 bg-white'}`}>

                                        {i === 1 && <div className="absolute top-0 right-0 w-1 h-full bg-[#ff5a0a]"></div>}

                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">
                                                {i === 1 ? 'TC' : 'Ad'}
                                            </div>
                                            <div className="flex-1">
                                                <div className="w-32 h-2 bg-gray-200 rounded-sm mb-1"></div>
                                                <div className="w-48 h-2 bg-gray-100 rounded-sm"></div>
                                            </div>
                                        </div>

                                        <div className={`w-3/4 h-5 rounded-sm mb-3 ${i === 1 ? 'bg-gradient-to-r from-[#ff5a0a] to-orange-400' : 'bg-blue-100'}`}></div>
                                        <div className="space-y-2">
                                            <div className="w-full h-2 bg-gray-100 rounded-sm"></div>
                                            <div className="w-5/6 h-2 bg-gray-100 rounded-sm"></div>
                                        </div>

                                        {i === 1 && (
                                            <div className="mt-4 flex items-center gap-2 text-[#ff5a0a] text-xs font-bold uppercase tracking-wider animate-pulse">
                                                <TrendingUp size={12} /> ניתוח CTR • קמפיין פעיל
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Floating Stats Badge */}
                            <div className="absolute bottom-6 left-6 bg-gray-900/95 backdrop-blur-md text-white p-5 rounded-xl shadow-2xl z-20 border border-gray-800 animate-in fade-in slide-in-from-bottom-4 delay-300">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#ff5a0a] mb-2 flex items-center gap-2">
                                    <Activity size={12} /> ביצועים
                                </div>
                                <div className="text-4xl font-black mb-1">18.4%</div>
                                <div className="text-xs text-gray-400 font-medium">CTR אורגני (7 ימים)</div>
                                <div className="w-full bg-gray-800 h-1 mt-3 rounded-full overflow-hidden">
                                    <div className="w-[70%] h-full bg-[#ff5a0a]"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 text-right">
                        <div className="inline-flex items-center gap-2 bg-orange-50 text-[#ff5a0a] border border-orange-100 px-4 py-2 text-xs font-bold uppercase tracking-widest mb-8 rounded-full shadow-sm hover:shadow-md transition-shadow cursor-default">
                            מנוע בינה SEO <Zap size={14} className="fill-[#ff5a0a]" />
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[0.9] mb-8 text-gray-900">
                            נתח <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5a0a] to-orange-500">Sessions</span><br />
                            אורגניים.
                        </h1>
                        <p className="text-xl text-gray-500 mb-10 max-w-lg leading-relaxed font-medium mr-0 ml-auto">
                            מחולל תנועה אורגנית לאנשי מקצוע בתחום SEO. נתח CTR, זמן שהייה ודפוסי מעורבות עם Sessions גלויים ב-GA4.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-end">
                            <a href="#demo" className="bg-white border border-gray-200 text-gray-900 px-8 py-4 text-sm font-bold uppercase tracking-wider hover:border-[#ff5a0a] hover:text-[#ff5a0a] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 rounded-sm group">
                                צפה בהדגמה חיה <Play size={16} className="group-hover:fill-[#ff5a0a]" />
                            </a>
                            <Link to="/signup" className="group bg-[#ff5a0a] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-black transition-all shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                                <ArrowRight size={18} className="group-hover:-translate-x-1 transition-transform rotate-180" />
                                הפעל קמפיין מדידה
                            </Link>
                        </div>

                        <div className="mt-8 flex items-center gap-4 text-xs font-bold text-gray-400 uppercase tracking-widest justify-end">
                            <div className="flex -space-x-2">
                                <div className="w-6 h-6 rounded-full bg-gray-200 border-2 border-white"></div>
                                <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white"></div>
                                <div className="w-6 h-6 rounded-full bg-gray-400 border-2 border-white"></div>
                            </div>
                            <span>בשימוש על ידי 15,000+ סוכנויות SEO</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 bg-gray-50 border-y border-gray-100" dir="ltr">
                <div className="max-w-5xl mx-auto text-left">
                    <span className="text-[#ff5a0a] font-bold tracking-wider uppercase text-xs mb-4 block">Technical quality notes</span>
                    <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">Organic-style traffic should support measurement, not promise rankings.</h2>
                    <div className="grid md:grid-cols-2 gap-8 text-gray-600 leading-relaxed font-medium">
                        <div className="space-y-4">
                            <p>
                                This Hebrew organic website traffic page is designed for agencies and site owners who need measurable visitor sessions for analytics review. A campaign can define country, device mix, session duration, scroll depth, bounce-rate range, and optional multi-page paths. Those settings make the traffic easier to compare against paid campaigns, referral sources, and direct traffic in Google Analytics 4.
                            </p>
                            <p>
                                Residential traffic is not a ranking guarantee. The useful SEO value is operational: cleaner source profiles, visible sessions, and enough behavioral data to evaluate landing pages. Teams can use this information to compare headlines, content depth, internal links, conversion paths, and regional demand before they spend more budget on advertising or content production.
                            </p>
                        </div>
                        <div className="space-y-4">
                            <p>
                                Quality controls matter because cheap datacenter traffic often creates distorted reports. Short sessions, one-page visits, repeated user agents, and unrealistic country mixes can make analysis harder. Traffic Creator focuses on configurable residential sessions, transparent campaign settings, and GA4-visible reporting so performance reviews have a clearer baseline.
                            </p>
                            <p>
                                The practical workflow is simple: start with a modest volume, choose one target page group, monitor real-time and acquisition reports, then compare engagement metrics against your content goals. If the traffic pattern looks useful, scale gradually and keep checking bounce rate, session duration, pages per session, geography, and conversion events.
                            </p>
                        </div>
                    </div>
                    <div className="mt-8 grid md:grid-cols-2 gap-8 text-gray-600 leading-relaxed font-medium">
                        <div className="space-y-4">
                            <p>
                                A good organic-style campaign should be documented before it starts. Record the target URL, selected countries, source mix, daily volume, expected session duration, and the conversion events you plan to review. This creates a falsifiable test: if the sessions do not appear in GA4, if geography does not match the campaign settings, or if engagement is too shallow, the campaign should be adjusted instead of scaled.
                            </p>
                            <p>
                                Agencies can use the same notes when reporting to clients. The useful evidence is not a vague promise of ranking movement. The useful evidence is a clear comparison of traffic source, landing-page behavior, scroll depth, internal navigation, and conversion activity before and after the campaign. That makes the workflow easier to defend and easier to improve.
                            </p>
                        </div>
                        <div className="space-y-4">
                            <p>
                                This page also avoids unsupported guarantees. Residential sessions can help teams review analytics, but they do not replace helpful content, technical SEO, internal linking, crawling health, or conversion research. Treat traffic generation as a measurement layer that supports those decisions. Keep the campaign modest, compare results carefully, and use the data to improve pages that already deserve real visitors.
                            </p>
                            <p>
                                For international campaigns, check language, country, device, and source consistency. A Hebrew page may need Israeli traffic, desktop and mobile splits, and session lengths that match the page type. Those details help avoid noisy reports and make the campaign useful for search experience optimization, content review, and landing-page quality checks.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Grid */}
            <section id="benefits" className="py-32 px-6 bg-white relative">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-24">
                        <h2 className="text-4xl font-black uppercase tracking-tight mb-6">למה למדוד תנועה אורגנית?</h2>
                        <div className="w-16 h-1.5 bg-[#ff5a0a] mx-auto mb-8 rounded-full"></div>
                        <p className="text-gray-500 max-w-2xl mx-auto text-lg leading-relaxed">
                            אלגוריתמי החיפוש מתפתחים. קישורים נכנסים לא מספיקים כדי להבין ביצועי עמודים. אתה צריך <span className="text-gray-900 font-bold">מדדי משתמש</span>. Traffic Creator מספק זמן שהייה, עומק גלילה ומדדי אינטראקציה לניתוח.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                        {benefits.map((b, i) => (
                            <div key={i} className="flex gap-8 p-10 border border-gray-100 hover:border-[#ff5a0a]/30 bg-white hover:bg-orange-50/20 shadow-sm hover:shadow-xl transition-all duration-300 group rounded-2xl relative overflow-hidden text-right">
                                <div className="absolute top-0 right-0 w-1 h-0 bg-[#ff5a0a] group-hover:h-full transition-all duration-300"></div>

                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#ff5a0a] transition-colors">{b.title}</h3>
                                    <p className="text-gray-500 leading-relaxed font-medium">
                                        {b.desc}
                                    </p>
                                </div>
                                <div className="shrink-0">
                                    <div className="bg-orange-50 w-16 h-16 flex items-center justify-center rounded-2xl group-hover:bg-[#ff5a0a] group-hover:rotate-6 transition-all duration-300 text-[#ff5a0a] group-hover:text-white shadow-sm">
                                        {b.icon}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technical Detail / Dark Mode Section */}
            <section className="py-32 px-6 bg-[#f9fafb] border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-20 items-center">
                        {/* Code Block Visual */}
                        <div className="bg-[#111] text-gray-300 p-2 rounded-xl shadow-2xl relative overflow-hidden group">
                            {/* Window Actions */}
                            <div className="flex items-center gap-2 p-4 border-b border-gray-800 bg-[#151515] rounded-t-lg">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                </div>
                                <div className="text-xs font-mono text-gray-500 mr-4 flex items-center gap-2">
                                    campaign_config.json <Code size={12} />
                                </div>
                            </div>

                            <div className="p-8 font-mono text-xs md:text-sm leading-relaxed overflow-x-auto text-right">
                                <div className="opacity-50 mb-4 text-[10px] uppercase tracking-widest">// הגדרות תנועה</div>
                                <div>{'}'} = <span className="text-blue-400">campaign</span> <span className="text-purple-400">const</span></div>
                                <div className="pr-4">
                                    ,<span className="text-green-400">"3 עליונות"</span> <span className="text-gray-400">:target</span>
                                </div>
                                <div className="pr-4">
                                    ] [
                                    <span className="text-gray-400">:keywords</span>
                                </div>
                                <div className="pr-8 text-orange-300">,"שירותי שיפור דירוג"</div>
                                <div className="pr-8 text-orange-300">,"רכישת תנועה אורגנית"</div>
                                <div className="pr-8 text-orange-300">"ניתוח CTR ב-SEO"</div>
                                <div className="pr-4">, ]</div>
                                <div className="pr-4">
                                    {'{'} <span className="text-gray-400">:behavior</span>
                                </div>
                                <div className="pr-8">
                                    ,<span className="text-yellow-300">"185ש'"</span> <span className="text-blue-300">:timeOnPage</span>
                                </div>
                                <div className="pr-8">
                                    ,<span className="text-yellow-300">"85%"</span> <span className="text-blue-300">:scrollDepth</span>
                                </div>
                                <div className="pr-8">
                                    <span className="text-yellow-300">"12%"</span> <span className="text-blue-300">:bounceRate</span>
                                </div>
                                <div className="pr-4">{'}'}</div>
                                <div>{';'}</div>

                                <div className="mt-6 flex items-center gap-2 text-green-500 text-[10px] font-bold uppercase tracking-widest justify-end">
                                    מהדר מוכן <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-12 text-right">
                            <div className="pr-8 border-r-2 border-gray-200 hover:border-[#ff5a0a] transition-colors duration-300">
                                <h3 className="text-2xl font-black uppercase tracking-tight mb-4 flex items-center gap-3 justify-end">
                                    מילות מפתח <Target className="text-[#ff5a0a]" size={24} />
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed font-medium">
                                    אל תשלח תנועה רק לכתובת URL. המערכת יכולה לדמות מסלולי חיפוש סביב מילות המפתח שלך, ואז להציג ב-GA4 נתוני Session, גלילה ומשך ביקור לצורך ניתוח.
                                </p>
                            </div>
                            <div className="pr-8 border-r-2 border-gray-200 hover:border-[#ff5a0a] transition-colors duration-300">
                                <h3 className="text-2xl font-black uppercase tracking-tight mb-4 flex items-center gap-3 justify-end">
                                    מסלולים מורכבים <Layout className="text-[#ff5a0a]" size={24} />
                                </h3>
                                <p className="text-gray-500 text-lg leading-relaxed font-medium">
                                    הגדר מסלולי דפים מרובים. מבקרים נוחתים בפוסט הבלוג שלך, קוראים 60 שניות, לוחצים לדף המחירים ואז מבקרים בטופס יצירת הקשר שלך.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Premium Comparison */}
            <section className="py-32 px-6 bg-white">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-4">איכות חשובה</h2>
                        <p className="text-gray-500">אל תסכן את סמכות הדומיין שלך עם תנועת בוטים זולה.</p>
                    </div>

                    <div className="bg-white shadow-[0_10px_60px_rgba(0,0,0,0.05)] rounded-2xl overflow-hidden border border-gray-100">
                        <div className="grid grid-cols-3 bg-gray-900 text-white p-6 text-xs font-bold uppercase tracking-widest">
                            <div className="text-gray-500 text-right">בוטים זולים</div>
                            <div className="text-[#ff5a0a] flex items-center gap-2 justify-center">Traffic Creator <Zap size={14} className="fill-[#ff5a0a]" /></div>
                            <div className="pr-4 text-right">תכונה</div>
                        </div>
                        <div className="divide-y divide-gray-50">
                            {comparisons.map((c, i) => (
                                <div key={i} className="grid grid-cols-3 p-6 items-center text-sm font-medium hover:bg-gray-50 transition-colors group">
                                    <div className="text-gray-400 flex items-center gap-3 pr-4 justify-end">
                                        {c.them} <X size={16} />
                                    </div>
                                    <div className="flex items-center gap-3 text-[#ff5a0a] font-bold bg-orange-50/50 -my-6 py-6 border-x border-orange-100/50 justify-center">
                                        {c.us} <CheckCircle size={18} className="mr-4" />
                                    </div>
                                    <div className="font-bold text-gray-900 flex items-center gap-3 pr-4 justify-end">
                                        {c.feature}
                                        <div className="w-1.5 h-1.5 rounded-full bg-gray-300 group-hover:bg-[#ff5a0a] transition-colors"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 px-6 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 px-4">
                        <span className="text-[#ff5a0a] font-bold tracking-wider uppercase text-xs mb-4 block">המלצות</span>
                        <h2 className="text-4xl font-black text-gray-900 mb-6">סיפורי הצלחה</h2>
                        <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                            גלה כיצד אנשי מקצוע מנתחים קמפיינים עם התנועה האורגנית שלנו.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=david_c`} alt="דוד כהן" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900 text-right">דוד כהן</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider text-right">מנהל סוכנות SEO</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6 text-right">"הלקוחות שלנו דורשים נתונים ברורים, וניתוח ה-CTR של TrafficCreator עוזר לנו לבדוק עמודים בצורה מסודרת יותר."</p>
                            <div className="flex gap-1 text-[#ff5a0a] justify-end">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=noa_l`} alt="נועה לוי" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900 text-right">נועה לוי</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider text-right">בונה אתרי נישה</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6 text-right">"הייתי סקפטית לגבי תנועה אוטומטית, אבל כתובות IP מגורים ומסלולי משתמש מורכבים כאן נותנים נתוני GA4 ברורים יותר."</p>
                            <div className="flex gap-1 text-[#ff5a0a] justify-end">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=yossi_m`} alt="יוסי מזרחי" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900 text-right">יוסי מזרחי</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider text-right">יועץ שיווק דיגיטלי</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6 text-right">"החלפנו ניסויי תנועה לא מדויקים בקמפיינים ממוקדים. זה דורש פחות תחזוקה ומספק מדדי זמן שהייה טובים יותר."</p>
                            <div className="flex gap-1 text-[#ff5a0a] justify-end">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-32 px-6 bg-[#ff5a0a] relative overflow-hidden">
                <div className="absolute inset-0 opacity-10"></div>

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-8 text-white">התחל למדוד עכשיו</h2>
                    <p className="text-xl text-white/90 mb-12 font-medium max-w-2xl mx-auto">
                        קבל בסיס נתונים ברור יותר עם מחולל תנועה אורגנית מתקדם וגלוי ב-GA4.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <Link to="/signup" className="bg-white text-black px-12 py-5 text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-all shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                            <ArrowRight size={18} className="rotate-180" /> התחל ניסיון חינם
                        </Link>
                        <Link to="/#pricing" className="bg-transparent border-2 border-white text-white px-12 py-5 text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-[#ff5a0a] transition-all rounded-sm flex items-center justify-center">
                            צפה במחירים
                        </Link>
                    </div>
                    <div className="mt-12 text-white/60 text-xs font-bold uppercase tracking-widest">
                        לא נדרש כרטיס אשראי לתקופת ניסיון • בקרות איכות • ביטול בכל עת
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-black text-white py-20 px-6 border-t border-gray-900">
                <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12 text-right">
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">חברה</h4>
                        <ul className="space-y-4 text-sm font-medium text-gray-300">
                            <li><Link to="/legal/impressum" className="hover:text-[#ff5a0a] transition-colors">אימפרסום</Link></li>
                            <li><Link to="/legal/terms" className="hover:text-[#ff5a0a] transition-colors">תנאי שימוש</Link></li>
                            <li><Link to="/legal/privacy" className="hover:text-[#ff5a0a] transition-colors">מדיניות פרטיות</Link></li>
                            <li><Link to="/helpdesk" className="hover:text-[#ff5a0a] transition-colors">מרכז עזרה</Link></li>
                            <li><Link to="/blog" className="hover:text-[#ff5a0a] transition-colors">בלוג SEO</Link></li>
                            <li><Link to="/about" className="hover:text-[#ff5a0a] transition-colors">אודותינו</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-8">פלטפורמה</h4>
                        <ul className="space-y-4 text-sm font-medium text-gray-300">
                            <li><Link to="/dashboard/affiliate" className="hover:text-[#ff5a0a] transition-colors">תוכנית שותפים</Link></li>
                            <li><Link to="/login" className="hover:text-[#ff5a0a] transition-colors">דשבורד</Link></li>
                            <li><Link to="/#pricing" className="hover:text-[#ff5a0a] transition-colors">מחירים</Link></li>
                            <li><Link to="/#workflow" className="hover:text-[#ff5a0a] transition-colors">איך זה עובד</Link></li>
                        </ul>
                    </div>
                    <div className="col-span-2">
                        <div className="text-3xl font-black text-white tracking-tight mb-6">TRAFFIC CREATOR</div>
                        <p className="text-gray-500 max-w-sm mb-8 mr-0 ml-auto">
                            הסטנדרט התעשייתי ליצירת תנועת SEO. אנחנו עוזרים לסוכנויות ובעלי אתרים לשפר את אותות נראות החיפוש שלהם בבטחה.
                        </p>
                        <div className="flex gap-4 justify-end">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-gray-500 hover:bg-[#ff5a0a] hover:text-white transition-colors cursor-pointer">
                                    <Globe size={16} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-gray-900 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-600">
                    <div className="flex gap-2 items-center">
                        <span>מערכות פעילות</span>
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    </div>
                    <div className="flex gap-4 text-gray-500">
                        <Link to="/free-website-traffic" className="hover:text-[#ff5a0a] transition-colors">תנועה חינם</Link>
                        <Link to="/organic-website-traffic" className="text-[#ff5a0a]">תנועה אורגנית</Link>
                        <Link to="/buy-website-traffic" className="hover:text-[#ff5a0a] transition-colors">רכישת תנועה</Link>
                    </div>
                    <div>© {new Date().getFullYear()} EasyTrafficBot UG (haftungsbeschränkt) · Amtsgericht Wuppertal, HRB 30863</div>
                </div>
            </footer>
        </div>
    );
};

export default HeOrganicWebsiteTraffic;
